/**
 * Alta y baja de dispositivos (tablets del negocio). Sólo la ve el dueño.
 *
 * Distinto del QR de mesa: esto es para que el personal entre por PIN desde
 * una tablet específica, no para que el comensal pida. El token sólo se
 * muestra una vez, al darla de alta — igual que una contraseña, en la base
 * sólo queda el hash.
 */
import { useEffect, useState } from 'react';
import { api } from '../lib/api';

interface Sucursal {
  id: number;
  nombre: string;
  tipo: 'restaurante' | 'abarrotes';
}

type Modo = 'caja' | 'cocina' | 'mesero' | 'repartidor' | 'admin';

interface Dispositivo {
  id: number;
  sucursal_id: number;
  sucursal_nombre: string;
  nombre: string;
  modo: Modo;
  activo: 0 | 1;
  ultimo_visto: string | null;
  creado_en: string;
}

const ETIQUETA_MODO: Record<Modo, string> = {
  caja: 'Caja',
  cocina: 'Cocina',
  mesero: 'Mesero',
  repartidor: 'Repartidor',
  admin: 'Panel del dueño',
};

export function Dispositivos({ sucursales }: { sucursales: Sucursal[] }) {
  const [dispositivos, setDispositivos] = useState<Dispositivo[]>([]);
  const [cargando, setCargando] = useState(true);
  const [mostrarForm, setMostrarForm] = useState(false);
  const [nombre, setNombre] = useState('');
  const [modo, setModo] = useState<Modo>('caja');
  const [sucursalId, setSucursalId] = useState<number | ''>('');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tokenNuevo, setTokenNuevo] = useState<{ nombre: string; token: string } | null>(null);

  async function cargar() {
    try {
      setCargando(true);
      setDispositivos(await api<Dispositivo[]>('/auth/dispositivos'));
    } catch (err) {
      console.error(err);
    } finally {
      setCargando(false);
    }
  }

  useEffect(() => {
    cargar();
  }, []);

  async function crear(e: React.FormEvent) {
    e.preventDefault();
    if (!nombre.trim() || !sucursalId) return;

    try {
      setGuardando(true);
      setError(null);
      const creado = await api<{ nombre: string; token: string }>('/auth/dispositivos', {
        method: 'POST',
        body: { nombre: nombre.trim(), modo, sucursalId },
      });
      setTokenNuevo(creado);
      setNombre('');
      setModo('caja');
      setSucursalId('');
      setMostrarForm(false);
      await cargar();
    } catch (err: any) {
      setError(err?.message || 'No se pudo dar de alta el dispositivo.');
    } finally {
      setGuardando(false);
    }
  }

  async function alternarActivo(d: Dispositivo) {
    if (
      d.activo &&
      !confirm(`¿Dar de baja "${d.nombre}"? Dejará de poder entrar por PIN desde esa tablet.`)
    ) {
      return;
    }
    try {
      await api(`/auth/dispositivos/${d.id}`, { method: 'PATCH', body: { activo: d.activo ? 0 : 1 } });
      await cargar();
    } catch (err: any) {
      alert(err?.message || 'No se pudo actualizar el dispositivo.');
    }
  }

  return (
    <div className="mx-auto max-w-3xl px-4 py-6">
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-titulo text-2xl font-extrabold text-tinta">Dispositivos</h1>
          <p className="text-sm text-salvia">
            Tablets del negocio dadas de alta. El PIN del personal sólo funciona desde una de éstas.
          </p>
        </div>
        <button
          onClick={() => setMostrarForm(!mostrarForm)}
          className="btn-principal self-start text-sm py-2 px-4"
        >
          {mostrarForm ? 'Cancelar' : '+ Nuevo Dispositivo'}
        </button>
      </div>

      {mostrarForm && (
        <form onSubmit={crear} className="mb-6 rounded-2xl border border-nopal/30 bg-white p-5 shadow-sm">
          <h3 className="font-titulo text-base font-bold text-tinta mb-4">Dar de alta dispositivo</h3>
          {error && <p className="mb-3 text-sm font-semibold text-barro-oscuro">{error}</p>}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <label className="mb-1 block text-xs font-bold text-salvia">Nombre *</label>
              <input
                type="text"
                required
                placeholder="Ej. Tablet Caja 1"
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
                className="campo text-base"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-bold text-salvia">Modo *</label>
              <select value={modo} onChange={(e) => setModo(e.target.value as Modo)} className="campo text-base">
                {Object.entries(ETIQUETA_MODO).map(([valor, etiqueta]) => (
                  <option key={valor} value={valor}>
                    {etiqueta}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-bold text-salvia">Sucursal *</label>
              <select
                required
                value={sucursalId}
                onChange={(e) => setSucursalId(e.target.value ? Number(e.target.value) : '')}
                className="campo text-base"
              >
                <option value="">Elige…</option>
                {sucursales.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.nombre}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="mt-4 flex justify-end">
            <button disabled={guardando} type="submit" className="btn-principal text-sm py-2 px-6">
              {guardando ? 'Guardando…' : 'Guardar Dispositivo'}
            </button>
          </div>
        </form>
      )}

      {cargando ? (
        <p className="py-12 text-center text-salvia">Cargando dispositivos…</p>
      ) : dispositivos.length === 0 ? (
        <div className="rounded-2xl border border-salvia/20 bg-white p-8 text-center text-salvia">
          <p className="text-base font-semibold">No hay dispositivos registrados todavía.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {dispositivos.map((d) => (
            <div
              key={d.id}
              className="flex items-center justify-between rounded-2xl border border-salvia/20 bg-white p-4 shadow-sm"
            >
              <div>
                <div className="flex items-center gap-2">
                  <p className="font-titulo text-base font-bold text-tinta">{d.nombre}</p>
                  <span className="rounded-full bg-nopal-claro px-2 py-0.5 text-xs font-bold text-nopal-oscuro">
                    {ETIQUETA_MODO[d.modo]}
                  </span>
                  {!d.activo && (
                    <span className="rounded-full bg-barro/20 px-2 py-0.5 text-xs font-bold text-barro-oscuro">
                      Dado de baja
                    </span>
                  )}
                </div>
                <p className="mt-1 text-xs text-salvia">
                  {d.sucursal_nombre}
                  {d.ultimo_visto ? ` · Últ. uso: ${new Date(d.ultimo_visto).toLocaleString()}` : ' · Sin usar todavía'}
                </p>
              </div>
              <button
                onClick={() => alternarActivo(d)}
                className={`shrink-0 rounded-lg border px-3 py-1.5 text-xs font-bold ${
                  d.activo
                    ? 'border-barro/30 text-barro-oscuro hover:bg-barro-claro'
                    : 'border-nopal/30 text-nopal-oscuro hover:bg-nopal-claro'
                }`}
              >
                {d.activo ? 'Dar de baja' : 'Reactivar'}
              </button>
            </div>
          ))}
        </div>
      )}

      {tokenNuevo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-tinta/60 p-4 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-3xl bg-white p-6 shadow-2xl">
            <h3 className="font-titulo text-xl font-bold text-tinta">Dispositivo registrado</h3>
            <p className="mt-1 text-sm text-salvia">
              Copia este código ahora en la tablet <strong>{tokenNuevo.nombre}</strong>. No se vuelve a
              mostrar.
            </p>
            <div className="my-4 break-all rounded-xl border-2 border-nopal/30 bg-cal p-4 font-mono text-sm text-tinta">
              {tokenNuevo.token}
            </div>
            <button
              onClick={() => setTokenNuevo(null)}
              className="w-full rounded-xl bg-nopal py-2.5 font-bold text-white hover:bg-nopal-oscuro"
            >
              Ya lo copié, cerrar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
